import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Wallet, Calculator, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY = {
  nome: '',
  descrizione_breve: '',
  tipo_calcolo: 'DF',
  giorni: 30,
  rate: '',
  note: '',
};

const TIPO_LABELS = {
  DF: 'Data fattura (DF)',
  FM: 'Fine mese (FM)',
  DFFM: 'Data fattura → fine mese (DFFM)',
  NONE: 'Immediato / Descrittivo',
};

const TIPO_BADGE_CLASS = {
  DF: 'bg-blue-100 text-blue-700 border-blue-200',
  FM: 'bg-purple-100 text-purple-700 border-purple-200',
  DFFM: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  NONE: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function ModalitaPagamentoPage() {
  const [modalita, setModalita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [simulOpen, setSimulOpen] = useState(false);
  const [simulData, setSimulData] = useState(new Date().toISOString().slice(0, 10));
  const [simulResult, setSimulResult] = useState(null);
  const [simulBusy, setSimulBusy] = useState(false);
  const [simulModalitaId, setSimulModalitaId] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/modalita-pagamento`, { withCredentials: true });
      setModalita(r.data || []);
    } catch (e) {
      toast.error('Errore nel caricamento modalità');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const grouped = useMemo(() => {
    const order = ['NONE', 'DF', 'FM', 'DFFM'];
    const result = {};
    order.forEach(k => { result[k] = []; });
    (modalita || []).forEach(m => {
      const k = order.includes(m.tipo_calcolo) ? m.tipo_calcolo : 'NONE';
      result[k].push(m);
    });
    Object.values(result).forEach(arr => arr.sort((a, b) => (a.giorni || 0) - (b.giorni || 0)));
    return result;
  }, [modalita]);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      nome: m.nome || '',
      descrizione_breve: m.descrizione_breve || '',
      tipo_calcolo: m.tipo_calcolo || 'DF',
      giorni: m.giorni || 0,
      rate: Array.isArray(m.rate) ? m.rate.join(',') : '',
      note: m.note || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error('Nome obbligatorio');
      return;
    }
    setBusy(true);
    try {
      const ratesArr = form.rate
        ? form.rate.split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n) && n > 0)
        : null;
      const payload = {
        nome: form.nome.trim(),
        descrizione_breve: form.descrizione_breve.trim(),
        tipo_calcolo: form.tipo_calcolo,
        giorni: parseInt(form.giorni, 10) || 0,
        rate: ratesArr && ratesArr.length > 0 ? ratesArr : null,
        note: form.note.trim(),
      };
      if (editingId) {
        await axios.put(`${API}/modalita-pagamento/${editingId}`, payload, { withCredentials: true });
        toast.success('Modalità aggiornata');
      } else {
        await axios.post(`${API}/modalita-pagamento`, payload, { withCredentials: true });
        toast.success('Modalità creata');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Eliminare la modalità "${m.nome}"?\n\nNon verrà rimossa dai DDT/Fatture che la usano già.`)) return;
    try {
      await axios.delete(`${API}/modalita-pagamento/${m.id}`, { withCredentials: true });
      toast.success('Modalità eliminata');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Ripopolare le 19 modalità predefinite mancanti?')) return;
    try {
      const r = await axios.post(`${API}/modalita-pagamento/seed-defaults`, {}, { withCredentials: true });
      toast.success(`${r.data.created} modalità ripristinate (totale default: ${r.data.total_default})`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore seed');
    }
  };

  const openSimulator = (m) => {
    setSimulModalitaId(m.id);
    setSimulResult(null);
    setSimulOpen(true);
  };

  const runSimulation = async () => {
    if (!simulData || !simulModalitaId) return;
    setSimulBusy(true);
    try {
      const r = await axios.post(`${API}/modalita-pagamento/calcola`, {
        data_fattura: simulData,
        modalita_id: simulModalitaId,
      }, { withCredentials: true });
      setSimulResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore calcolo');
    } finally {
      setSimulBusy(false);
    }
  };

  const formatDate = (iso) => iso ? iso.split('-').reverse().join('-') : '-';

  return (
    <div className="space-y-6" data-testid="modalita-pagamento-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-slate-700" />
            Modalità di Pagamento
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Configura le formule di calcolo della scadenza usate in DDT e Fatture ricevute.
            <br />
            Sintassi: <b>DF</b> = data fattura + giorni · <b>FM</b> = fine mese + giorni · <b>DFFM</b> = data fattura + giorni → fine mese del risultato.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeed} data-testid="seed-defaults">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Ripristina predefinite
          </Button>
          <Button onClick={openNew} className="bg-slate-900 text-white" data-testid="new-modalita">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova modalità
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm p-8 text-center">Caricamento…</div>
      ) : (
        ['NONE', 'DF', 'FM', 'DFFM'].map(tipo => (
          grouped[tipo].length > 0 && (
            <Card key={tipo}>
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Badge variant="outline" className={TIPO_BADGE_CLASS[tipo]}>{tipo}</Badge>
                  {TIPO_LABELS[tipo]}
                  <span className="text-xs font-normal text-slate-400">({grouped[tipo].length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Sigla</TableHead>
                      <TableHead className="text-right">Giorni</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grouped[tipo].map(m => (
                      <TableRow key={m.id} data-testid={`modalita-row-${m.id}`}>
                        <TableCell className="font-medium">{m.nome}</TableCell>
                        <TableCell><span className="text-xs font-mono text-slate-600">{m.descrizione_breve || '-'}</span></TableCell>
                        <TableCell className="text-right font-mono">{m.giorni || 0}</TableCell>
                        <TableCell>
                          {Array.isArray(m.rate) && m.rate.length > 0
                            ? <span className="text-xs font-mono">{m.rate.join(' / ')} gg</span>
                            : <span className="text-slate-300 text-xs">-</span>}
                        </TableCell>
                        <TableCell>
                          {m.is_default
                            ? <Badge variant="outline" className="bg-slate-50 text-slate-600 text-[10px]">Predefinita</Badge>
                            : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Custom</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => openSimulator(m)} title="Simula scadenza" data-testid={`simulate-${m.id}`}>
                              <Calculator className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(m)} title="Modifica" data-testid={`edit-${m.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(m)} title="Elimina" data-testid={`delete-${m.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        ))
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica modalità' : 'Nuova modalità di pagamento'}</DialogTitle>
            <DialogDescription>
              Definisci la formula di calcolo della scadenza per pagamenti fornitore.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} required placeholder="es. 45 giorni data fattura" data-testid="form-nome" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Sigla breve</Label>
                <Input value={form.descrizione_breve} onChange={e => setForm({ ...form, descrizione_breve: e.target.value })} placeholder="45 gg DF" data-testid="form-sigla" />
              </div>
              <div className="space-y-1">
                <Label>Tipo calcolo *</Label>
                <Select value={form.tipo_calcolo} onValueChange={v => setForm({ ...form, tipo_calcolo: v })}>
                  <SelectTrigger data-testid="form-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">NONE — immediato / descrittivo</SelectItem>
                    <SelectItem value="DF">DF — data fattura + giorni</SelectItem>
                    <SelectItem value="FM">FM — fine mese + giorni</SelectItem>
                    <SelectItem value="DFFM">DFFM — data fattura + giorni → fine mese</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Giorni</Label>
                <Input type="number" min="0" value={form.giorni} onChange={e => setForm({ ...form, giorni: e.target.value })} placeholder="30" data-testid="form-giorni" />
              </div>
              <div className="space-y-1">
                <Label>Rate (giorni, separati da virgola)</Label>
                <Input value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} placeholder="30,60,90" data-testid="form-rate" />
                <p className="text-[11px] text-slate-400">Lascia vuoto per pagamento in un'unica soluzione.</p>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Note interne (opzionale)" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={busy} className="bg-slate-900 text-white" data-testid="form-save">
                {editingId ? 'Salva' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Simulator dialog */}
      <Dialog open={simulOpen} onOpenChange={setSimulOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Simula scadenza
            </DialogTitle>
            <DialogDescription>
              Inserisci una data fattura e calcola la scadenza applicando la formula della modalità.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Data fattura di riferimento</Label>
              <Input type="date" value={simulData} onChange={e => setSimulData(e.target.value)} data-testid="simul-data" />
            </div>
            <Button onClick={runSimulation} disabled={simulBusy || !simulData} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="simul-run">
              {simulBusy ? 'Calcolo…' : 'Calcola scadenza'}
            </Button>
            {simulResult && (
              <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="simul-result">
                <div className="text-xs uppercase text-slate-500 font-semibold">Risultato</div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Modalità</span>
                  <span className="font-medium">{simulResult.modalita_nome}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Formula</span>
                  <Badge variant="outline" className="font-mono text-[11px]">{simulResult.formula_applicata}</Badge>
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-sm text-slate-600">Scadenza finale</span>
                  <span className="font-bold text-emerald-700">{formatDate(simulResult.scadenza)}</span>
                </div>
                {simulResult.scadenze && simulResult.scadenze.length > 1 && (
                  <div className="text-xs text-slate-500 pt-1 border-t">
                    <b>Rate multiple:</b> {simulResult.scadenze.map(s => formatDate(s)).join(' · ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
